import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Send, ArrowUpRight, Phone, Mail } from "lucide-react";
import { PROFILE, TRUSTED } from "@/data/content";
import { Reveal } from "@/components/bits/Reveal";
import { Sticker, Tape, HandNote } from "@/components/bits/Sticker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Smiley = () => (
  <motion.div
    data-testid="contact-smiley"
    className="w-28 h-28 md:w-36 md:h-36 rounded-full bg-[var(--yellow)] border-[1.5px] border-[var(--ink)] hard-shadow mx-auto flex items-center justify-center"
    initial={{ scale: 0, rotate: -30 }}
    whileInView={{ scale: 1, rotate: 0 }}
    whileHover={{ rotate: [0, -8, 8, 0] }}
    viewport={{ once: true }}
    transition={{ type: "spring", stiffness: 200, damping: 14 }}
  >
    <svg viewBox="0 0 100 100" className="w-2/3 h-2/3" fill="none" stroke="var(--ink)" strokeWidth="5" strokeLinecap="round">
      <rect x="28" y="28" width="9" height="20" fill="var(--ink)" stroke="none" />
      <rect x="63" y="28" width="9" height="20" fill="var(--ink)" stroke="none" />
      <path d="M28 66 Q50 86 72 66" />
    </svg>
  </motion.div>
);

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", project: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success("Got it! Anushka reads every message.");
      setForm({ name: "", email: "", project: "", message: "" });
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(Array.isArray(detail) ? detail[0]?.msg : detail || "Something slipped. Try again?");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="contact" className="relative max-w-6xl mx-auto px-6 md:px-10 py-24 md:py-36" data-testid="contact-section">
      <Reveal className="text-center mb-12">
        <HandNote className="text-2xl block mb-4">trusted by</HandNote>
        <div className="flex flex-wrap justify-center gap-3 mb-16" data-testid="trusted-logos">
          {TRUSTED.map((t, i) => (
            <span key={t.name} className="sticker !normal-case !tracking-tight font-pixel !text-base" style={{ background: t.bg, color: t.fg, "--r": `${i % 2 ? 2 : -2}deg` }}>
              {t.name}
            </span>
          ))}
        </div>
        <Smiley />
        <h2 className="pixel-title text-5xl sm:text-6xl md:text-8xl mt-8">Let's talk</h2>
        <p className="font-hand text-2xl md:text-3xl text-[var(--ink-60)] mt-4 max-w-xl mx-auto leading-snug">
          Got a project, a hard problem, or just want to swap behind-the-scenes stories? Write to me. I read every message.
        </p>
      </Reveal>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
        <Reveal className="hidden lg:block lg:col-span-3 pt-10">
          <div className="polaroid" style={{ "--r": "-5deg" }}>
            <Tape rotate={-6} className="-top-3 left-1/2 -translate-x-1/2" color="blue" />
            <img src={`${process.env.PUBLIC_URL || ""}/images/lets-talk-location.jpg`} alt="Anushka on location" className="!aspect-[3/4]" />
            <HandNote className="absolute bottom-2 left-3 text-lg">on location, between takes</HandNote>
          </div>
        </Reveal>

        <Reveal className="lg:col-span-6" delay={0.1}>
          <form onSubmit={submit} className="relative bg-white border-[1.5px] border-[var(--ink)] hard-shadow p-6 md:p-8" data-testid="contact-form">
            <Tape rotate={-3} className="-top-3 left-1/2 -translate-x-1/2 !w-28" />
            <div className="font-mono text-[0.62rem] tracking-[0.2em] uppercase text-[var(--ink-60)] mb-5 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-[var(--green)] border border-[var(--ink)]" /> leave a note on the desk
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input data-testid="contact-name" className="input-note" placeholder="your name" required minLength={2} value={form.name} onChange={set("name")} />
              <input data-testid="contact-email" className="input-note" placeholder="your email" type="email" required value={form.email} onChange={set("email")} />
            </div>
            <input data-testid="contact-project" className="input-note mt-4" placeholder="what's the project? (optional)" value={form.project} onChange={set("project")} />
            <textarea data-testid="contact-message" className="input-note mt-4 min-h-[140px] resize-y" placeholder="tell me about it…" required minLength={10} value={form.message} onChange={set("message")} />
            <div className="mt-6 flex items-center justify-between gap-4 flex-wrap">
              <button type="submit" disabled={busy} className="btn-ink disabled:opacity-60" data-testid="contact-submit">
                <Send size={13} /> {busy ? "Sending…" : sent ? "Send another" : "Send it"}
              </button>
              <HandNote className="text-xl">{sent ? "received — thank you!" : "usually replies within a day"}</HandNote>
            </div>
          </form>
        </Reveal>

        <Reveal className="lg:col-span-3 flex flex-col gap-3 items-start lg:items-end pt-4" delay={0.15}>
          <a href={`mailto:${PROFILE.email}`} data-testid="contact-email-link"><Sticker color="yellow" rotate={-2} icon={<Mail size={12} />} className="!normal-case !tracking-normal">{PROFILE.email}</Sticker></a>
          <a href={`tel:${PROFILE.phone.replace(/\s/g, "")}`} data-testid="contact-phone-link"><Sticker color="pink" rotate={2} icon={<Phone size={12} />} className="!normal-case !tracking-normal">{PROFILE.phone}</Sticker></a>
          {PROFILE.socials.map((s, i) => (
            <a key={s.label} href={s.href} target="_blank" rel="noreferrer" data-testid={`social-${s.label.toLowerCase()}`}>
              <Sticker color={["blue", "green", "purple", "paper"][i]} rotate={i % 2 ? 2 : -2} icon={<ArrowUpRight size={12} />}>{s.label}</Sticker>
            </a>
          ))}
          <div className="hidden lg:block polaroid mt-8 w-60" style={{ "--r": "4deg" }}>
            <Tape rotate={8} className="-top-3 right-4" color="pink" />
            <img src={`${process.env.PUBLIC_URL || ""}/images/mission-startab.jpg`} alt="Mission StartAb team together on set" style={{ aspectRatio: "2016 / 908" }} />
            <HandNote className="absolute bottom-2 left-3 text-lg">Mission StartAb</HandNote>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
